import FeedBackLink from '@/components/Approval/LeftContainer/FilterList/FeedBackLink';
import React, { useRef } from 'react';
import { Box, Stack } from '@mui/material';
import { useAtom } from 'jotai';
import { approvalFilter } from '@/recoil/atom';
import { useTranslation } from 'react-i18next';
import { ModalRef } from '@/components/@IntranetLibrary/Modal';
import useModal from '@/hooks/useModal';
import useFavoriteApproval from '@/components/Approval/@hooks/useFavoriteApproval';
import { INIT_WEB_APPROVAL_FILTER_STATE } from '@/recoil/init_data';
import ApprovalFiltersSkeleton from '@/components/Approval/Content/ApprovalFIlters/ApprovalFiltersSkeleton';
import { StyledEmpty } from '@/components/Approval/Content/ApprovalFIlters/styles';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import SvgImage from '@/components/Module/SvgImage';
import CreateApprovalModal from '@/components/Approval/CreateApprovalModal';
import CategoryContent from '@/components/Approval/CategoryContent';
import { ApprovalFilterType } from '@/types/api';
import { EventPageAreaName, WorkEventName } from '@/constant/gtm';
import { useWorkEventTracker } from '@/hooks/gtm/useGTMEventTracker';

type ApprovalFiltersProps = {
  onClick(id: number): void;
  isLoading: boolean;
  doApprovalCount?: number;
  ingApprovalCount?: number;
  myRefererCount?: number;
  bookMarkCount?: number;
  temporaryCount?: number;
  showCompletedDot?: boolean;
  showRejectedDot?: boolean;
  onDocumentSubmitted?: (docId: string) => void;
  dataItemChange?: (id: string, changes: Record<string, any>) => void;
};

const ApprovalFilters = ({
  onClick,
  isLoading,
  doApprovalCount,
  ingApprovalCount,
  myRefererCount,
  bookMarkCount,
  temporaryCount,
  showCompletedDot,
  showRejectedDot,
  onDocumentSubmitted,
  dataItemChange,
}: ApprovalFiltersProps) => {
  const pageArea = EventPageAreaName.WORK_APPROVAL;
  const workEventTracker = useWorkEventTracker(pageArea);
  const [filters, setFilters] = useAtom(approvalFilter);
  const { id: selectedId } = filters;
  const { t } = useTranslation();

  const createApprovalModal = useRef<ModalRef>(null);
  const { openModal: openCreateApprovalModal } = useModal(createApprovalModal);

  const { data: favoriteData } = useFavoriteApproval();

  const haveFavoriteData = (favoriteData?.data ?? []).length > 0;

  const handleFavoriteClick = (id: string) => {
    const tempFavoriteData = favoriteData?.data.find((item) => item.id === id);
    const params = tempFavoriteData?.params;
    const filterName = tempFavoriteData?.name;

    setFilters({
      ...INIT_WEB_APPROVAL_FILTER_STATE,
      ...params,
      keyword: params?.keyword ?? '',
      id: id,
      filterName: filterName,
    });
  };

  const sendGTMEvent = (contents: string) => {
    // GA Work - Approval 8번
    workEventTracker(WorkEventName.clickEvent, {
      area_name: 'Approval 목록',
      contents,
      status: undefined,
      description: '품의 필터',
    });
  };

  const handleClick = (id: number, contents: string) => {
    onClick(id);
    sendGTMEvent(contents);
  };

  return (
    <Box>
      <BoxButton
        variant="primary"
        size="extraLarge"
        startIcon={<SvgImage name={'document'} />}
        onClick={() => {
          openCreateApprovalModal();

          // GA Work - Approval 7번
          workEventTracker(WorkEventName.clickEvent, {
            area_name: 'Approval 목록',
            contents: '새 품의 작성',
            status: undefined,
            description: '새 품의 작성',
          });
        }}
        style={{ width: '100%', marginTop: 32 }}
      >
        {t('새 품의 작성')}
      </BoxButton>

      {isLoading && <ApprovalFiltersSkeleton />}

      {/* Category Drawer */}
      {!isLoading && (
        <Stack direction={'column'}>
          <CategoryContent
            id={ApprovalFilterType['즐겨찾기']}
            title={t('즐겨찾기')}
            onClick={(id) => handleClick(id, '즐겨찾기')}
            count={bookMarkCount}
            selectedId={selectedId}
          />
          <CategoryContent
            id={ApprovalFilterType['내 필터']}
            title={t('내 필터')}
            onClick={(id) => handleClick(id, '내 필터')}
            selectedId={selectedId}
          >
            {haveFavoriteData ? (
              favoriteData?.data.map((res, idx) => (
                <CategoryContent
                  key={`favorite_filter-${idx}`}
                  id={res.id}
                  title={res.name}
                  onClick={(id) => {
                    handleFavoriteClick(id);
                    sendGTMEvent(res.name);
                  }}
                  selectedId={selectedId}
                  isChildren
                  favoriteId={res.id}
                />
              ))
            ) : (
              <StyledEmpty>
                {t(
                  '‘전체 문서’에서 자주 쓰는 필터를 설정해\n내 필터로 저장해 보세요.',
                )}
              </StyledEmpty>
            )}
          </CategoryContent>
          <CategoryContent
            id={ApprovalFilterType['전체 문서']}
            title={t('전체 문서')}
            onClick={(id) => handleClick(id, '전체 문서')}
            selectedId={selectedId}
          />
          <CategoryContent
            id={ApprovalFilterType['내가 결재자인']}
            title={t('내가 결재자인')}
            onClick={(id) => handleClick(id, '내가 결재자인')}
            selectedId={selectedId}
          >
            <CategoryContent
              id={ApprovalFilterType['결재할 문서']}
              title={t('결재할 문서')}
              onClick={(id) => handleClick(id, '결재할 문서')}
              selectedId={selectedId}
              count={doApprovalCount}
              isChildren
            />
            <CategoryContent
              id={ApprovalFilterType['결재한 문서']}
              title={t('결재한 문서')}
              onClick={(id) => handleClick(id, '결재한 문서')}
              selectedId={selectedId}
              isChildren
            />
            <CategoryContent
              id={ApprovalFilterType['협조결재한 문서']}
              title={t('협조결재한 문서')}
              onClick={(id) => handleClick(id, '협조결재한 문서')}
              selectedId={selectedId}
              isChildren
            />
          </CategoryContent>
          <CategoryContent
            id={ApprovalFilterType['내가 작성자인']}
            title={t('내가 작성자인')}
            onClick={(id) => handleClick(id, '내가 작성자인')}
            selectedId={selectedId}
          >
            <CategoryContent
              id={ApprovalFilterType['진행중 문서']}
              title={t('진행중 문서')}
              onClick={(id) => handleClick(id, '진행중 문서')}
              selectedId={selectedId}
              count={ingApprovalCount}
              isChildren
            />
            <CategoryContent
              id={ApprovalFilterType['임시저장 문서']}
              title={t('임시저장 문서')}
              onClick={(id) => handleClick(id, '임시저장 문서')}
              selectedId={selectedId}
              count={temporaryCount}
              isChildren
            />
            <CategoryContent
              id={ApprovalFilterType['완료 문서']}
              title={t('완료 문서')}
              onClick={(id) => handleClick(id, '완료 문서')}
              selectedId={selectedId}
              showDot={showCompletedDot}
              isChildren
            />
            <CategoryContent
              id={ApprovalFilterType['반려 문서']}
              title={t('반려 문서')}
              onClick={(id) => handleClick(id, '반려 문서')}
              selectedId={selectedId}
              showDot={showRejectedDot}
              isChildren
            />
          </CategoryContent>
          <CategoryContent
            id={ApprovalFilterType['내가 참조자인 문서']}
            title={t('내가 참조자인 문서')}
            onClick={(id) => handleClick(id, '내가 참조자인 문서')}
            selectedId={selectedId}
            count={myRefererCount}
          />
          <CategoryContent
            id={ApprovalFilterType['팀원의 작성 문서']}
            title={t('팀원의 작성 문서')}
            onClick={(id) => handleClick(id, '팀원의 작성 문서')}
            selectedId={selectedId}
          />
          <CategoryContent
            id={ApprovalFilterType['팀원의 협조결재 문서']}
            title={t('팀원의 협조결재 문서')}
            onClick={(id) => handleClick(id, '팀원의 협조결재 문서')}
            selectedId={selectedId}
          />
        </Stack>
      )}

      <FeedBackLink pageArea={pageArea} />

      <CreateApprovalModal
        ref={createApprovalModal}
        onDocumentSubmitted={onDocumentSubmitted}
        dataItemChange={dataItemChange}
      />
    </Box>
  );
};
export default ApprovalFilters;
